import { useSearch } from '@tanstack/react-location'
import { useFormik } from 'formik'
import ActionSuccessModal from 'MediaManager/components/ActionSuccessModal'
import Input from 'MediaManager/components/Form/Input'
import JSONEditor from 'MediaManager/components/JSONEditor'
import ScreenLayout from 'MediaManager/layouts/ScreenLayout'
import { deleteLevel, upsertLevel } from 'MediaManager/services/levels'
import { getUnitType } from 'MediaManager/services/property'
import React, { useEffect, useMemo, useState } from 'react'
import Button from 'shared/components/Button'
import Loader from 'shared/components/Loader'
import Select from 'shared/components/Select'
import Toggle from 'shared/components/Toggle'
import { convertStringNumbersToNumbers } from 'shared/helpers/general'
import { errorMessages } from 'shared/settings'
import * as Yup from 'yup'

const levelMainFields = [
  'display_name',
  'floor_number',
  'size_of_balcony',
  'exterior_space_size',
  'height',
  'is_entrance',
  'original_floor_plan_path',
  'physical_floor_plan_path',
  'revit_floor_plan_path',
  'physical_floor_plan',
]

function LevelEditScreen(props) {
  const { id, unit_type_id } = useSearch()
  const [line, setLine] = useState()
  const [level, setLevel] = useState()
  const [resultValue, setResultValue] = useState()
  const [isLoading, setIsLoading] = useState(true)
  useEffect(() => {
    ;(async () => {
      if (unit_type_id) {
        const { data } = await getUnitType(unit_type_id)
        setLine(data.unit_types[0])
        if (id) {
          const level = data.unit_types[0].unit_type_levels.find((l) => l.id === Number(id))
          setLevel(level)
          formik.setValues(
            levelMainFields.reduce((acc, field) => {
              acc[field] = level[field]
              return acc
            }, {}),
          )
        }
      }
      setIsLoading(false)
    })()
  }, [id, unit_type_id])

  const validationSchema = Yup.object().shape({
    floor_number: Yup.number().required(errorMessages.required),
  })

  const formik = useFormik({
    initialValues: {},
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema,
    initialTouched: false,
    validateOnMount: true,
    onSubmit: async () => {
      try {
        await upsertLevel({
          ...(id ? { id } : {}),
          unit_type_id: unit_type_id,
          ...convertStringNumbersToNumbers(formik.values),
          is_entrance: formik.values.is_entrance ? true : false,
        })
        setResultValue('success')
      } catch (error) {
        console.error(error)
        setResultValue('error')
      }
    },
  })

  const isValid = useMemo(() => {
    return Object.values(formik.errors).every((v) => !v)
  }, [formik])

  function getFieldError(field) {
    return formik.errors[field] && formik.touched[field] ? formik.errors[field] : null
  }

  async function onDelete() {
    if (window.confirm('Are you sure you want to delete this level?')) {
      try {
        await deleteLevel(id)
        setResultValue('success')
      } catch (error) {
        setResultValue('error')
      }
    }
  }

  return (
    <ScreenLayout title="Edit/Add level">
      {resultValue && (
        <ActionSuccessModal
          action={`Level`}
          successText="has been successfully upserted."
          errorText="upserting failed. Please try again."
          state={resultValue}
          error={'Please try again later.'}
          skipConfirm
          skipBack
          onClose={() => {
            if (resultValue === 'success') {
              window.history.back()
            } else {
              setResultValue(null)
            }
          }}
        />
      )}
      {isLoading ? (
        <div>
          <Loader />
        </div>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <div className="grid grid-cols-2 gap-[16px]">
            {id && <Input disabled label="ID" wrapClassName="col-span-2" value={id} />}

            <Input disabled label="Property" wrapClassName="col-span-2" value={line?.property.property_name} />
            <Input
              disabled
              label="Line"
              wrapClassName="col-span-2"
              value={line?.name || line?.nick_name || line?.display_name}
            />
            <div className="flex items-center">
              <div className="min-w-max flex items-center mt-[16px]">
                <Toggle
                  name="is_entrance"
                  checked={formik.values.is_entrance}
                  onChange={() => {
                    formik.setFieldValue('is_entrance', !formik.values.is_entrance)
                  }}
                />
                <div className="text-sm  opacity-50 ml-[16px]">Is Entrance</div>
              </div>
              <Input
                number
                integer
                required
                label="Floor number"
                error={getFieldError('floor_number')}
                onChange={formik.handleChange}
                wrapClassName="flex-1 ml-[16px]"
                onBlur={formik.handleBlur}
                name="floor_number"
                value={formik.values.floor_number}
              />
            </div>
            <Input
              label="Level name"
              name="display_name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.display_name}
            />
            <Input
              label="Size of balcony"
              wrapClassName="col-span-1"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="size_of_balcony"
              number
              value={formik.values.size_of_balcony}
            />
            <Input
              label="Exterior space size"
              name="exterior_space_size"
              number
              wrapClassName="col-span-1"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value-={formik.values.exterior_space_size}
            />
            <Input
              label="Height"
              name="height"
              number
              wrapClassName="col-span-2"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.height}
            />
            <Input
              label="Original floor plan image path "
              wrapClassName="col-span-2"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="original_floor_plan_path"
              value={formik.values.original_floor_plan_path}
            />
            <Input
              label="Physical floor plan json path "
              wrapClassName="col-span-2"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="physical_floor_plan_path"
              value={formik.values.physical_floor_plan_path}
            />
            <Input
              label="Revit floor plan path"
              name="revit_floor_plan_path"
              wrapClassName="col-span-2"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.revit_floor_plan_path}
            />
            <div className="col-span-2" onChange={formik.handleChange}>
              <JSONEditor
                label="Upload Physical JSON"
                onChange={(value) => formik.setFieldValue('physical_floor_plan', value)}
                value={formik.values.physical_floor_plan}
                json={level?.physical_floor_plan}
                onDelete={() => {
                  formik.setFieldValue('physical_floor_plan', null)
                  setLevel({ ...level, physical_floor_plan: null })
                }}
              />
            </div>
          </div>
          <div
            className={`flex justify-end items-center mt-[16px] ${
              formik.isSubmitting ? 'pointer-events-none  opacity-50' : ''
            }`}
          >
            {id && (
              <Button rounded variant="secondary" className="mr-[16px]" onClick={onDelete}>
                Delete
              </Button>
            )}
            <Button rounded variant="secondary" onClick={() => window.history.back()}>
              Cancel
            </Button>
            <Button loading={formik.isSubmitting} disabled={!isValid} rounded className="ml-[16px]" type="submit">
              {id ? 'Update' : 'Add'}
            </Button>
          </div>
        </form>
      )}
    </ScreenLayout>
  )
}

export default LevelEditScreen
